import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import OrganizerLayout from '../../components/layouts/OrganizerLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  MessageSquare, Send, Users, User, Search, Paperclip, 
  Image as ImageIcon, X, Loader2, ArrowLeft, Crown
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OrganizerMessages = () => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('direct');
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [attendees, setAttendees] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [events, setEvents] = useState([]);
  const [groupChat, setGroupChat] = useState(null);
  const [groupMessages, setGroupMessages] = useState([]);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);

  useEffect(() => {
    fetchEvents();
    fetchConversations();
    setupWebSocket();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  useEffect(() => {
    if (selectedEvent) {
      fetchAttendees();
      fetchGroupChat();
    }
  }, [selectedEvent]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, groupMessages]);

  const setupWebSocket = () => {
    if (!token) return;
    
    const wsUrl = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    wsRef.current = new WebSocket(`${wsUrl}/api/ws/messages/${token}`);
    
    wsRef.current.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'new_message' && data.conversation_id === selectedConversation?.id) {
        setMessages(prev => [...prev, data.message]);
      } else if (data.type === 'group_message' && data.event_id === selectedEvent?.id) {
        setGroupMessages(prev => [...prev, data.message]);
      }
    };
    
    wsRef.current.onclose = () => {
      setTimeout(setupWebSocket, 3000);
    };
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/events/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(response.data);
      if (response.data.length > 0) {
        setSelectedEvent(response.data[0]);
      }
    } catch (error) {
      console.error('Failed to fetch events:', error);
    }
  };

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/messages/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendees = async () => {
    if (!selectedEvent) return;
    try {
      const response = await axios.get(`${API_URL}/api/event/attendees?event_id=${selectedEvent.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAttendees(response.data.attendees || response.data || []);
    } catch (error) {
      console.error('Failed to fetch attendees:', error);
    }
  };

  const fetchGroupChat = async () => {
    if (!selectedEvent) return;
    try {
      const response = await axios.get(`${API_URL}/api/messages/group/${selectedEvent.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGroupChat(response.data);
      fetchGroupMessages();
    } catch (error) {
      console.error('Failed to fetch group chat:', error);
    }
  };

  const fetchGroupMessages = async () => {
    if (!selectedEvent) return;
    try {
      const response = await axios.get(`${API_URL}/api/messages/group/${selectedEvent.id}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGroupMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch group messages:', error);
    }
  };

  const selectConversation = async (conv) => {
    setSelectedConversation(conv);
    try {
      const response = await axios.get(`${API_URL}/api/messages/conversations/${conv.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      toast.error('Failed to load messages');
    }
  };

  const startConversation = async (attendee) => {
    try {
      const response = await axios.post(`${API_URL}/api/messages/conversations`, 
        { participant_id: attendee.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedConversation({ ...response.data, other_user: attendee });
      setMessages([]);
      fetchConversations();
    } catch (error) {
      toast.error('Failed to start conversation');
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || sending) return;
    
    setSending(true);
    try {
      if (activeTab === 'direct' && selectedConversation) {
        const response = await axios.post(
          `${API_URL}/api/messages/conversations/${selectedConversation.id}`,
          { content: newMessage },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMessages(prev => [...prev, response.data]);
      } else if (activeTab === 'group' && selectedEvent) {
        const response = await axios.post(
          `${API_URL}/api/messages/group/${selectedEvent.id}`,
          { content: newMessage },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setGroupMessages(prev => [...prev, response.data]);
      }
      setNewMessage('');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const filteredAttendees = attendees.filter(a => 
    `${a.first_name} ${a.last_name}`.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    }
    return date.toLocaleDateString();
  };

  return (
    <OrganizerLayout>
      <div className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <MessageSquare className="h-8 w-8 text-maroon" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Messages</h1>
            <p className="text-gray-600">Communicate with your attendees</p>
          </div>
        </div>

        {/* Event Selector */}
        {events.length > 1 && (
          <div className="mb-4">
            <select
              value={selectedEvent?.id || ''}
              onChange={(e) => setSelectedEvent(events.find(ev => ev.id === e.target.value))}
              className="px-4 py-2 border rounded-lg bg-white"
            >
              {events.map(ev => (
                <option key={ev.id} value={ev.id}>{ev.event_name}</option>
              ))}
            </select>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="direct" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Direct Messages
            </TabsTrigger>
            <TabsTrigger value="group" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Group Chat
            </TabsTrigger>
          </TabsList>

          {/* Direct Messages Tab */}
          <TabsContent value="direct">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-[600px]">
              {/* Conversations List */}
              <Card className="lg:col-span-1 flex flex-col">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Conversations</CardTitle>
                  <div className="relative mt-2">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search attendees..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </CardHeader>
                <CardContent className="flex-1 overflow-hidden p-2">
                  <ScrollArea className="h-full">
                    {/* Existing Conversations */}
                    {conversations.map(conv => (
                      <div
                        key={conv.id}
                        onClick={() => selectConversation(conv)}
                        className={`p-3 rounded-lg cursor-pointer mb-2 transition-colors ${
                          selectedConversation?.id === conv.id 
                            ? 'bg-maroon/10 border border-maroon' 
                            : 'hover:bg-gray-100'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Avatar>
                            <AvatarImage src={conv.other_user?.profile_photo} />
                            <AvatarFallback>
                              {conv.other_user?.first_name?.[0]}{conv.other_user?.last_name?.[0]}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">
                              {conv.other_user?.first_name} {conv.other_user?.last_name}
                            </p>
                            <p className="text-sm text-gray-500 truncate">
                              {conv.last_message_preview || 'No messages yet'}
                            </p>
                          </div>
                          {conv.unread_count > 0 && (
                            <Badge className="bg-maroon">{conv.unread_count}</Badge>
                          )}
                        </div>
                      </div>
                    ))}

                    {/* Start New Conversation */}
                    {searchQuery && filteredAttendees.length > 0 && (
                      <div className="mt-4 pt-4 border-t">
                        <p className="text-sm text-gray-500 mb-2">Start new conversation:</p>
                        {filteredAttendees.slice(0, 5).map(attendee => (
                          <div
                            key={attendee.id}
                            onClick={() => startConversation(attendee)}
                            className="p-2 rounded-lg cursor-pointer hover:bg-gray-100 flex items-center gap-2"
                          >
                            <Avatar className="h-8 w-8">
                              <AvatarFallback className="text-xs">
                                {attendee.first_name?.[0]}{attendee.last_name?.[0]}
                              </AvatarFallback>
                            </Avatar>
                            <span className="text-sm">{attendee.first_name} {attendee.last_name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>

              {/* Messages Area */}
              <Card className="lg:col-span-2 flex flex-col">
                {selectedConversation ? (
                  <>
                    <CardHeader className="pb-2 border-b">
                      <div className="flex items-center gap-3">
                        <Avatar>
                          <AvatarImage src={selectedConversation.other_user?.profile_photo} />
                          <AvatarFallback>
                            {selectedConversation.other_user?.first_name?.[0]}
                            {selectedConversation.other_user?.last_name?.[0]}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <CardTitle className="text-lg">
                            {selectedConversation.other_user?.first_name} {selectedConversation.other_user?.last_name}
                          </CardTitle>
                          <p className="text-sm text-gray-500">{selectedConversation.other_user?.email}</p>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="flex-1 overflow-hidden p-0">
                      <ScrollArea className="h-[400px] p-4">
                        {messages.map((msg, idx) => (
                          <div
                            key={msg.id || idx}
                            className={`mb-4 flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                          >
                            <div className={`max-w-[70%] ${
                              msg.sender_id === user?.id 
                                ? 'bg-maroon text-white' 
                                : 'bg-gray-100 text-gray-900'
                            } rounded-lg p-3`}>
                              <p>{msg.content}</p>
                              <p className={`text-xs mt-1 ${
                                msg.sender_id === user?.id ? 'text-white/70' : 'text-gray-500'
                              }`}>
                                {formatTime(msg.created_at)}
                              </p>
                            </div>
                          </div>
                        ))}
                        <div ref={messagesEndRef} />
                      </ScrollArea>
                    </CardContent>
                    <div className="p-4 border-t">
                      <div className="flex gap-2">
                        <Input
                          placeholder="Type a message..."
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                          className="flex-1"
                        />
                        <Button onClick={sendMessage} disabled={sending} className="bg-maroon hover:bg-maroon/90">
                          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center text-gray-500">
                    <div className="text-center">
                      <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>Select a conversation or search for an attendee</p>
                    </div>
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>

          {/* Group Chat Tab */}
          <TabsContent value="group">
            <Card className="h-[600px] flex flex-col">
              <CardHeader className="pb-2 border-b">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-maroon/10 flex items-center justify-center">
                    <Users className="h-5 w-5 text-maroon" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">
                      {groupChat?.name || `${selectedEvent?.event_name} Group Chat`}
                    </CardTitle>
                    <p className="text-sm text-gray-500">{attendees.length} members</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="flex-1 overflow-hidden p-0">
                <ScrollArea className="h-[420px] p-4">
                  {groupMessages.map((msg, idx) => (
                    <div key={msg.id || idx} className="mb-4">
                      <div className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[70%] ${
                          msg.sender_id === user?.id 
                            ? 'bg-maroon text-white' 
                            : 'bg-gray-100 text-gray-900'
                        } rounded-lg p-3`}>
                          {msg.sender_id !== user?.id && (
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm font-medium">
                                {msg.sender_name}
                              </span>
                              {msg.sender_role === 'organizer' && (
                                <Badge variant="outline" className="text-xs">
                                  <Crown className="h-3 w-3 mr-1" />
                                  Organizer
                                </Badge>
                              )}
                            </div>
                          )}
                          <p>{msg.content}</p>
                          <p className={`text-xs mt-1 ${
                            msg.sender_id === user?.id ? 'text-white/70' : 'text-gray-500'
                          }`}>
                            {formatTime(msg.created_at)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </ScrollArea>
              </CardContent>
              <div className="p-4 border-t">
                <div className="flex gap-2">
                  <Input
                    placeholder="Type a message to the group..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                    className="flex-1"
                  />
                  <Button onClick={sendMessage} disabled={sending} className="bg-maroon hover:bg-maroon/90">
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </OrganizerLayout>
  );
};

export default OrganizerMessages;
